import { isNotDefined } from '@sgme/fp';
import { DealSplitGrid } from 'components/common-form/builder/form/node/field/DealSplit/DealSplitGrid';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import type { DealSplitRow } from 'models/forms/DealSplitData';
import type { DealSplitGridData, DealSplitGridMetadata } from 'models/forms/fields';
import { FC, useCallback } from 'react';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import { patchDealSplitGridRow } from './utils';

export const DealSplitField: FC<ProductNodeBuilderProps> = ({ node: { id }, tabIndex }) => {
  const [gridData, setGridData] = useData<DealSplitGridData>(id);
  const [tradeDate] = useData<Nullable<string>>('TradeDate');
  const metadata = useFieldMetadata<DealSplitGridMetadata>(id)!;

  const hasIndependentAmount =
    gridData?.some(row => row.IndependentAmount !== null && row.IndependentAmount !== undefined) ?? false;

  const availableCustodians = metadata?.availableCustodians ?? [];

  const onRowCustodianChanged = useCallback(
    (rowIndex: number, custodianId: string) => {
      setGridData(patchDealSplitGridRow(gridData, rowIndex, { Custodian: custodianId }));
    },
    [gridData, setGridData],
  );

  const onRowNotionalChanged = useCallback(
    (rowIndex: number, notional?: number) => {
      setGridData(patchDealSplitGridRow(gridData, rowIndex, { NotionalAmountInGrid: notional }));
    },
    [gridData, setGridData],
  );

  const onRowAdded = useCallback(() => {
    setGridData([
      ...gridData,
      {
        Custodian: undefined,
        NotionalAmountInGrid: undefined,
        IndependentAmount: hasIndependentAmount
          ? { Type: undefined, Value: undefined, ValueDate: undefined }
          : undefined,
      },
    ]);
  }, [gridData, hasIndependentAmount, setGridData]);

  const onRowIndependentAmountChanged = useCallback(
    (rowIndex: number, independentAmount: DealSplitRow['independentAmount']) => {
      setGridData(patchDealSplitGridRow(gridData, rowIndex, { IndependentAmount: independentAmount }));
    },
    [gridData, setGridData],
  );

  const onRowDeleted = useCallback(
    (rowIndex: number) => gridData && setGridData([...gridData.slice(0, rowIndex), ...gridData.slice(rowIndex + 1)]),
    [gridData, setGridData],
  );

  if (isNotDefined(gridData) || metadata === undefined) {
    return null;
  }

  const { currency, notionalToSplit } = metadata;

  const rows: DealSplitRow[] = gridData.map(x => ({
    custodianId: !availableCustodians.find(custodian => custodian.id === x.Custodian) ? undefined : x.Custodian,
    notional: x.NotionalAmountInGrid,
    independentAmount: x.IndependentAmount,
    availableCustodians,
  }));

  const allocatedNotional = gridData.reduce((acc, cur) => acc + (cur.NotionalAmountInGrid || 0), 0);

  const unallocatedNotional = notionalToSplit - allocatedNotional;

  const canAdd = gridData.length < availableCustodians.length;
  const canDelete = gridData.length > 1;

  return (
    <DealSplitGrid
      fieldId={id}
      onRowAdded={onRowAdded}
      onRowDeleted={onRowDeleted}
      onRowCustodianChanged={onRowCustodianChanged}
      onRowNotionalChanged={onRowNotionalChanged}
      onRowIndependentAmountChanged={onRowIndependentAmountChanged}
      currency={currency}
      key={id}
      dealSplits={rows}
      hasIndependentAmount={hasIndependentAmount}
      tradeDate={tradeDate}
      canAddRow={canAdd}
      canDeleteRow={canDelete}
      notionalToSplit={notionalToSplit}
      unallocatedNotional={unallocatedNotional}
      tabIndex={tabIndex}
    />
  );
};
DealSplitField.displayName = 'DealSplitField';
